import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  Users, 
  DollarSign, 
  Calendar,
  Edit,
  Eye,
  Banknote,
  CreditCard,
  UserPlus,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Wallet,
  Building2,
  MinusCircle,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';

import { API } from '@/lib/api';

const SalaryMaster = ({ user }) => {
  // Employees state
  const [employees, setEmployees] = useState([]);
  const [employeeSummary, setEmployeeSummary] = useState({ total_employees: 0, active_count: 0, total_monthly_salary: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employees');
  
  // Monthly salaries state
  const [monthlySalaries, setMonthlySalaries] = useState([]);
  const [salarySummary, setSalarySummary] = useState({ total_gross: 0, total_paid: 0, total_pending: 0 });
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Dialogs
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [showPaySalary, setShowPaySalary] = useState(false);
  const [showPayAdvance, setShowPayAdvance] = useState(false);
  const [showGenerateSalaries, setShowGenerateSalaries] = useState(false);
  
  // Selected items
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedSalary, setSelectedSalary] = useState(null);
  
  // Form states
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    designation: '',
    monthly_salary: '',
    phone: '',
    bank_account: '',
    bank_name: '',
    joining_date: ''
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank',
    bank_account: '',
    payment_reference: '',
    remarks: ''
  });
  
  const [generateMonth, setGenerateMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Deductions state for salary generation
  const [preparedEmployees, setPreparedEmployees] = useState([]);
  const [employeeDeductions, setEmployeeDeductions] = useState({});
  const [loadingPrepare, setLoadingPrepare] = useState(false);
  
  // Loading states to prevent double-click
  const [creatingEmployee, setCreatingEmployee] = useState(false);
  const [updatingEmployee, setUpdatingEmployee] = useState(false);
  const [generatingSalaries, setGeneratingSalaries] = useState(false);
  const [payingSalary, setPayingSalary] = useState(false);
  const [deletingMonthSalaries, setDeletingMonthSalaries] = useState(false);
  const [payingAdvance, setPayingAdvance] = useState(false);

  const canManage = ['admin', 'accountant', 'general_manager'].includes(user?.role);

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees || []);
      setEmployeeSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  };

  // Fetch monthly salaries
  const fetchSalaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/salaries`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month: selectedMonth }
      });
      setMonthlySalaries(response.data.salaries || []);
      setSalarySummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to fetch salaries');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchEmployees(), fetchSalaries()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchSalaries();
  }, [selectedMonth]);

  // Create employee
  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    if (creatingEmployee) return; // Prevent double-click
    if (!newEmployee.name || !newEmployee.designation || !newEmployee.monthly_salary) {
      toast.error('Name, designation, and salary are required');
      return;
    }
    
    try {
      setCreatingEmployee(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employees`, {
        ...newEmployee,
        monthly_salary: parseFloat(newEmployee.monthly_salary)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Employee added successfully');
      setShowAddEmployee(false);
      setNewEmployee({ name: '', designation: '', monthly_salary: '', phone: '', bank_account: '', bank_name: '', joining_date: '' });
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create employee'));
    } finally {
      setCreatingEmployee(false);
    }
  };

  // Update employee
  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    if (updatingEmployee) return; // Prevent double-click
    if (!selectedEmployee) return;
    
    try {
      setUpdatingEmployee(true);
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/employees/${selectedEmployee.id}`, {
        name: newEmployee.name,
        designation: newEmployee.designation,
        monthly_salary: parseFloat(newEmployee.monthly_salary),
        phone: newEmployee.phone,
        bank_account: newEmployee.bank_account,
        bank_name: newEmployee.bank_name,
        joining_date: newEmployee.joining_date,
        status: newEmployee.status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Employee updated successfully');
      setShowEditEmployee(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update employee'));
    } finally {
      setUpdatingEmployee(false);
    }
  };

  // Prepare salary generation - fetch employees with advance balances
  const prepareSalaryGeneration = async () => {
    try {
      setLoadingPrepare(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/salaries/prepare/${generateMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreparedEmployees(response.data.employees || []);
      
      // Initialize deductions for each employee
      const initialDeductions = {};
      (response.data.employees || []).forEach(emp => {
        initialDeductions[emp.id] = {
          advance_recovery: 0,
          pf_deduction: 0,
          esi_deduction: 0,
          absence_deduction: 0,
          other_deductions: 0
        };
      });
      setEmployeeDeductions(initialDeductions);
      
      if (response.data.already_generated > 0) {
        toast.info(`${response.data.already_generated} employees already have salaries for this month`);
      }
      
      setLoadingPrepare(false);
      setShowGenerateSalaries(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to prepare salary generation'));
      setLoadingPrepare(false);
    }
  };

  // Update deduction for an employee
  const updateDeduction = (employeeId, field, value) => {
    setEmployeeDeductions(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Calculate net salary for an employee
  const calculateNetSalary = (employee) => {
    const ded = employeeDeductions[employee.id] || {};
    const totalDeductions = (ded.advance_recovery || 0) + (ded.pf_deduction || 0) + 
                           (ded.esi_deduction || 0) + (ded.absence_deduction || 0) + 
                           (ded.other_deductions || 0);
    return Math.max(0, employee.monthly_salary - totalDeductions);
  };

  // Generate monthly salaries with deductions
  const handleGenerateSalaries = async () => {
    if (generatingSalaries) return; // Prevent double-click
    
    try {
      setGeneratingSalaries(true);
      const token = localStorage.getItem('token');
      
      // Build employee deductions array
      const employeeDeductionsList = preparedEmployees.map(emp => ({
        employee_id: emp.id,
        ...employeeDeductions[emp.id]
      }));
      
      const response = await axios.post(`${API}/salaries/generate-with-deductions`, {
        month: generateMonth,
        employee_deductions: employeeDeductionsList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Generated ${response.data.generated} salary records (${response.data.skipped} skipped)`);
      setShowGenerateSalaries(false);
      setPreparedEmployees([]);
      setEmployeeDeductions({});
      setSelectedMonth(generateMonth);
      fetchSalaries();
      fetchEmployees(); // Refresh to update advance balances
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate salaries'));
    } finally {
      setGeneratingSalaries(false);
    }
  };

  // Pay salary
  const handlePaySalary = async () => {
    if (payingSalary) return; // Prevent double-click
    if (!selectedSalary || !paymentForm.amount) {
      toast.error('Please enter amount');
      return;
    }
    
    try {
      setPayingSalary(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/salaries/pay`, {
        employee_id: selectedSalary.employee_id,
        monthly_salary_id: selectedSalary.id,
        amount: parseFloat(paymentForm.amount),
        payment_type: 'salary',
        payment_method: paymentForm.payment_method,
        bank_account: paymentForm.bank_account || null,
        payment_reference: paymentForm.payment_reference || null,
        remarks: paymentForm.remarks || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowPaySalary(false);
      setSelectedSalary(null);
      setPaymentForm({ amount: '', payment_method: 'bank', bank_account: '', payment_reference: '', remarks: '' });
      fetchSalaries();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to pay salary'));
    } finally {
      setPayingSalary(false);
    }
  };

  // Delete month salaries (Admin only)
  const handleDeleteMonthSalaries = async () => {
    if (deletingMonthSalaries) return; // Prevent double-click
    if (!window.confirm(`Are you sure you want to delete ALL salary records for ${selectedMonth}? This will:\n\n• Delete all ${monthlySalaries.length} salary records\n• Delete any salary payments made\n• Reverse advance adjustments\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      setDeletingMonthSalaries(true);
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/salaries/month/${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchSalaries();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete salaries'));
    } finally {
      setDeletingMonthSalaries(false);
    }
  };

  // Pay advance
  const handlePayAdvance = async () => {
    if (payingAdvance) return; // Prevent double-click
    if (!selectedEmployee || !paymentForm.amount) {
      toast.error('Please enter amount');
      return;
    }
    
    try {
      setPayingAdvance(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/salaries/pay`, {
        employee_id: selectedEmployee.id,
        amount: parseFloat(paymentForm.amount),
        payment_type: 'advance',
        payment_method: paymentForm.payment_method,
        bank_account: paymentForm.bank_account || null,
        payment_reference: paymentForm.payment_reference || null,
        remarks: paymentForm.remarks || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowPayAdvance(false);
      setSelectedEmployee(null);
      setPaymentForm({ amount: '', payment_method: 'bank', bank_account: '', payment_reference: '', remarks: '' });
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to pay advance'));
    } finally {
      setPayingAdvance(false);
    }
  };

  // View employee details
  const viewEmployeeDetails = async (employee) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/${employee.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmployee(response.data);
      setShowEmployeeDetails(true);
    } catch (error) {
      toast.error('Failed to fetch employee details');
    }
  };

  // Open edit dialog
  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setNewEmployee({
      name: employee.name,
      designation: employee.designation,
      monthly_salary: employee.monthly_salary.toString(),
      phone: employee.phone || '',
      bank_account: employee.bank_account || '',
      bank_name: employee.bank_name || '',
      joining_date: employee.joining_date || '',
      status: employee.status
    });
    setShowEditEmployee(true);
  };

  // Open pay salary dialog
  const openPaySalaryDialog = (salary) => {
    setSelectedSalary(salary);
    setPaymentForm({
      amount: salary.balance.toString(),
      payment_method: 'bank',
      bank_account: '',
      payment_reference: '',
      remarks: ''
    });
    setShowPaySalary(true);
  };

  // Open pay advance dialog
  const openPayAdvanceDialog = (employee) => {
    setSelectedEmployee(employee);
    setPaymentForm({
      amount: '',
      payment_method: 'bank',
      bank_account: '',
      payment_reference: '',
      remarks: ''
    });
    setShowPayAdvance(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      terminated: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700',
      partially_paid: 'bg-blue-100 text-blue-700',
      paid: 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ').toUpperCase()}</Badge>;
  };

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="salary-master-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Salary Master
          </h1>
          <p className="text-gray-500 text-sm">Manage employees and salary payments</p>
        </div>
        
        {canManage && (
          <div className="flex gap-2">
            <Button onClick={() => {
              setGenerateMonth(new Date().toISOString().slice(0, 7));
              prepareSalaryGeneration();
            }} variant="outline" disabled={loadingPrepare}>
              <Calendar className="w-4 h-4 mr-2" />
              {loadingPrepare ? 'Loading...' : 'Generate Monthly'}
            </Button>
            <Button onClick={() => setShowAddEmployee(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="employees" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Employees
          </TabsTrigger>
          <TabsTrigger value="salaries" className="flex items-center gap-1">
            <Banknote className="w-4 h-4" />
            Monthly Salaries
            {salarySummary.pending_count > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-xs">
                {salarySummary.pending_count}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees" className="mt-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-600 font-medium">Total Employees</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{employeeSummary.total_employees}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600 font-medium">Active</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{employeeSummary.active_count}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-600 font-medium">Monthly Payroll</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">{formatCurrency(employeeSummary.total_monthly_salary)}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-orange-600 font-medium">Advances Outstanding</span>
                </div>
                <p className="text-2xl font-bold text-orange-700">
                  {formatCurrency(employees.reduce((sum, e) => sum + (e.advance_balance || 0), 0))}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Employees Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Employee List
              </CardTitle>
            </CardHeader>
            <CardContent>
              {employees.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No employees found. Click "Add Employee" to get started.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead className="text-right">Monthly Salary</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Bank Account</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead className="text-right">Advance Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell>{emp.designation}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(emp.monthly_salary)}</TableCell>
                        <TableCell>{emp.phone || '-'}</TableCell>
                        <TableCell className="text-sm">
                          {emp.bank_account ? (
                            <span>{emp.bank_name ? `${emp.bank_name}: ` : ''}{emp.bank_account}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {emp.joining_date ? new Date(emp.joining_date).toLocaleDateString('en-IN') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {(emp.advance_balance || 0) > 0 ? (
                            <span className="text-orange-600 font-semibold">{formatCurrency(emp.advance_balance)}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(emp.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => viewEmployeeDetails(emp)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(emp)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {/* Advance Pay button hidden - use Bank Transactions or Day Closings instead */}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Salaries Tab */}
        <TabsContent value="salaries" className="mt-4">
          {/* Month Selector and Summary */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Label>Month:</Label>
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-48"
              />
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-600 font-medium">Total Gross</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(salarySummary.total_gross)}</p>
                <p className="text-xs text-blue-500">{salarySummary.count} employees</p>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600 font-medium">Paid</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(salarySummary.total_paid)}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-600 font-medium">Pending</span>
                </div>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(salarySummary.total_pending)}</p>
                <p className="text-xs text-red-500">{salarySummary.pending_count} pending</p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-200">
              <CardContent className="pt-4">
                <Button 
                  onClick={() => {
                    setGenerateMonth(selectedMonth);
                    setShowGenerateSalaries(true);
                  }}
                  className="w-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Generate for {selectedMonth}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Salaries Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Banknote className="w-5 h-5" />
                  Salary Records - {selectedMonth}
                </CardTitle>
                {user?.role === 'admin' && monthlySalaries.length > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={handleDeleteMonthSalaries}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All ({monthlySalaries.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {monthlySalaries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No salary records for this month. Click "Generate for {selectedMonth}" to create salary records.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead className="text-right">Gross Salary</TableHead>
                      <TableHead className="text-right">Deductions</TableHead>
                      <TableHead className="text-right">Net Salary</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlySalaries.map((salary) => (
                      <TableRow key={salary.id} data-testid={`salary-row-${salary.id}`}>
                        <TableCell className="font-medium">{salary.employee_name}</TableCell>
                        <TableCell>{salary.designation}</TableCell>
                        <TableCell className="text-right">{formatCurrency(salary.gross_salary)}</TableCell>
                        <TableCell className="text-right">
                          {salary.deductions > 0 ? formatCurrency(salary.deductions) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(salary.net_salary)}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {salary.paid_amount > 0 ? formatCurrency(salary.paid_amount) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {salary.balance > 0 ? formatCurrency(salary.balance) : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(salary.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Employee Dialog */}
      <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New Employee
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEmployee} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                  placeholder="Employee name"
                />
              </div>
              <div>
                <Label>Designation *</Label>
                <Input
                  value={newEmployee.designation}
                  onChange={(e) => setNewEmployee({...newEmployee, designation: e.target.value})}
                  placeholder="e.g., Chef, Waiter"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Salary *</Label>
                <Input
                  type="number"
                  value={newEmployee.monthly_salary}
                  onChange={(e) => setNewEmployee({...newEmployee, monthly_salary: e.target.value})}
                  placeholder="e.g., 25000"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                  placeholder="Phone number"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={newEmployee.bank_name}
                  onChange={(e) => setNewEmployee({...newEmployee, bank_name: e.target.value})}
                  placeholder="e.g., HDFC Bank"
                />
              </div>
              <div>
                <Label>Bank Account</Label>
                <Input
                  value={newEmployee.bank_account}
                  onChange={(e) => setNewEmployee({...newEmployee, bank_account: e.target.value})}
                  placeholder="Account number"
                />
              </div>
            </div>
            
            <div>
              <Label>Joining Date</Label>
              <Input
                type="date"
                value={newEmployee.joining_date}
                onChange={(e) => setNewEmployee({...newEmployee, joining_date: e.target.value})}
              />
            </div>
            
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddEmployee(false)}>Cancel</Button>
              <Button type="submit">Add Employee</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={showEditEmployee} onOpenChange={setShowEditEmployee}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Employee
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEmployee} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Designation *</Label>
                <Input
                  value={newEmployee.designation}
                  onChange={(e) => setNewEmployee({...newEmployee, designation: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Salary *</Label>
                <Input
                  type="number"
                  value={newEmployee.monthly_salary}
                  onChange={(e) => setNewEmployee({...newEmployee, monthly_salary: e.target.value})}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={newEmployee.status} onValueChange={(v) => setNewEmployee({...newEmployee, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Joining Date</Label>
                <Input
                  type="date"
                  value={newEmployee.joining_date}
                  onChange={(e) => setNewEmployee({...newEmployee, joining_date: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={newEmployee.bank_name}
                  onChange={(e) => setNewEmployee({...newEmployee, bank_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Bank Account</Label>
                <Input
                  value={newEmployee.bank_account}
                  onChange={(e) => setNewEmployee({...newEmployee, bank_account: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditEmployee(false)}>Cancel</Button>
              <Button type="submit">Update Employee</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Employee Details Dialog */}
      <Dialog open={showEmployeeDetails} onOpenChange={setShowEmployeeDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Employee Details - {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Designation</Label>
                  <p className="font-medium">{selectedEmployee.designation}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p>{getStatusBadge(selectedEmployee.status)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Monthly Salary</Label>
                  <p className="font-bold text-lg">{formatCurrency(selectedEmployee.monthly_salary)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Advance Balance</Label>
                  <p className={`font-bold text-lg ${selectedEmployee.advance_balance > 0 ? 'text-orange-600' : ''}`}>
                    {formatCurrency(selectedEmployee.advance_balance)}
                  </p>
                </div>
              </div>

              {/* Recent Salary History */}
              {selectedEmployee.salary_history?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Recent Salary Records</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Net Salary</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedEmployee.salary_history.slice(0, 6).map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>{s.salary_month}</TableCell>
                          <TableCell className="text-right">{formatCurrency(s.net_salary)}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(s.paid_amount)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(s.balance)}</TableCell>
                          <TableCell>{getStatusBadge(s.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Recent Payments */}
              {selectedEmployee.payment_history?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Recent Payments</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Paid By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedEmployee.payment_history.slice(0, 10).map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{new Date(p.paid_at).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell>
                            <Badge variant={p.payment_type === 'advance' ? 'warning' : 'default'}>
                              {p.payment_type.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(p.amount)}</TableCell>
                          <TableCell>{p.payment_method}</TableCell>
                          <TableCell>{p.paid_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay Salary Dialog */}
      <Dialog open={showPaySalary} onOpenChange={setShowPaySalary}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Pay Salary - {selectedSalary?.employee_name}
            </DialogTitle>
          </DialogHeader>
          {selectedSalary && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-600">Month: <strong>{selectedSalary.salary_month}</strong></p>
                <p className="text-sm text-blue-600">Balance: <strong>{formatCurrency(selectedSalary.balance)}</strong></p>
              </div>
              
              <div>
                <Label>Amount to Pay *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  max={selectedSalary.balance}
                />
              </div>
              
              <div>
                <Label>Payment Method *</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {paymentForm.payment_method === 'bank' && (
                <>
                  <div>
                    <Label>Bank Account</Label>
                    <Input
                      value={paymentForm.bank_account}
                      onChange={(e) => setPaymentForm({...paymentForm, bank_account: e.target.value})}
                      placeholder="Bank account used"
                    />
                  </div>
                  <div>
                    <Label>Reference/UTR</Label>
                    <Input
                      value={paymentForm.payment_reference}
                      onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                      placeholder="Transaction reference"
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={paymentForm.remarks}
                  onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})}
                  placeholder="Optional notes"
                />
              </div>
              
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowPaySalary(false)}>Cancel</Button>
                <Button onClick={handlePaySalary}>
                  <Banknote className="w-4 h-4 mr-2" />
                  Pay Salary
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay Advance Dialog */}
      <Dialog open={showPayAdvance} onOpenChange={setShowPayAdvance}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Pay Advance - {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-4">
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-sm text-orange-600">
                  Current Advance Balance: <strong>{formatCurrency(selectedEmployee.advance_balance || 0)}</strong>
                </p>
              </div>
              
              <div>
                <Label>Advance Amount *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder="Enter advance amount"
                />
              </div>
              
              <div>
                <Label>Payment Method *</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {paymentForm.payment_method === 'bank' && (
                <div>
                  <Label>Reference/UTR</Label>
                  <Input
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                    placeholder="Transaction reference"
                  />
                </div>
              )}
              
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={paymentForm.remarks}
                  onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})}
                  placeholder="Reason for advance"
                />
              </div>
              
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowPayAdvance(false)}>Cancel</Button>
                <Button onClick={handlePayAdvance} className="bg-orange-600 hover:bg-orange-700">
                  <Wallet className="w-4 h-4 mr-2" />
                  Pay Advance
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Salaries Dialog with Deductions */}
      <Dialog open={showGenerateSalaries} onOpenChange={setShowGenerateSalaries}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Generate Monthly Salaries - {generateMonth}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg flex justify-between items-center">
              <p className="text-sm text-blue-600">
                Enter deductions for each employee. Salaries will be generated after confirmation.
              </p>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Month:</Label>
                <Input
                  type="month"
                  value={generateMonth}
                  onChange={async (e) => {
                    const newMonth = e.target.value;
                    setGenerateMonth(newMonth);
                    // Fetch employees for new month
                    try {
                      setLoadingPrepare(true);
                      const token = localStorage.getItem('token');
                      const response = await axios.get(`${API}/salaries/prepare/${newMonth}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setPreparedEmployees(response.data.employees || []);
                      const initialDeductions = {};
                      (response.data.employees || []).forEach(emp => {
                        initialDeductions[emp.id] = {
                          advance_recovery: 0, pf_deduction: 0, esi_deduction: 0,
                          absence_deduction: 0, other_deductions: 0
                        };
                      });
                      setEmployeeDeductions(initialDeductions);
                      setLoadingPrepare(false);
                    } catch (error) {
                      toast.error('Failed to fetch employees');
                      setLoadingPrepare(false);
                    }
                  }}
                  className="w-40"
                />
              </div>
            </div>
            
            {preparedEmployees.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No employees to generate salaries for. All active employees may already have salaries for this month.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-white">Employee</TableHead>
                        <TableHead className="text-right">Gross Salary</TableHead>
                        <TableHead className="text-right">
                          <div className="flex flex-col">
                            <span>Advance Recovery</span>
                            <span className="text-xs text-gray-400">(Balance)</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-right">PF</TableHead>
                        <TableHead className="text-right">ESI</TableHead>
                        <TableHead className="text-right">Absence/Late</TableHead>
                        <TableHead className="text-right">Other</TableHead>
                        <TableHead className="text-right">Total Deductions</TableHead>
                        <TableHead className="text-right">Net Salary</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preparedEmployees.map((emp) => {
                        const ded = employeeDeductions[emp.id] || {};
                        const totalDed = (ded.advance_recovery || 0) + (ded.pf_deduction || 0) + 
                                        (ded.esi_deduction || 0) + (ded.absence_deduction || 0) + 
                                        (ded.other_deductions || 0);
                        const netSalary = Math.max(0, emp.monthly_salary - totalDed);
                        
                        return (
                          <TableRow key={emp.id}>
                            <TableCell className="sticky left-0 bg-white">
                              <div>
                                <span className="font-medium">{emp.name}</span>
                                <span className="text-xs text-gray-500 block">{emp.designation}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(emp.monthly_salary)}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col items-end gap-1">
                                <Input
                                  type="number"
                                  className="w-24 text-right"
                                  value={ded.advance_recovery || ''}
                                  onChange={(e) => updateDeduction(emp.id, 'advance_recovery', e.target.value)}
                                  max={emp.advance_balance}
                                  placeholder="0"
                                />
                                {emp.advance_balance > 0 && (
                                  <span className="text-xs text-orange-600">
                                    Bal: {formatCurrency(emp.advance_balance)}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-24 text-right"
                                value={ded.pf_deduction || ''}
                                onChange={(e) => updateDeduction(emp.id, 'pf_deduction', e.target.value)}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-24 text-right"
                                value={ded.esi_deduction || ''}
                                onChange={(e) => updateDeduction(emp.id, 'esi_deduction', e.target.value)}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-24 text-right"
                                value={ded.absence_deduction || ''}
                                onChange={(e) => updateDeduction(emp.id, 'absence_deduction', e.target.value)}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-24 text-right"
                                value={ded.other_deductions || ''}
                                onChange={(e) => updateDeduction(emp.id, 'other_deductions', e.target.value)}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell className="text-right text-red-600 font-medium">
                              {totalDed > 0 ? formatCurrency(totalDed) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-bold text-green-600">
                              {formatCurrency(netSalary)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      
                      {/* Totals Row */}
                      <TableRow className="bg-gray-50 font-bold">
                        <TableCell className="sticky left-0 bg-gray-50">
                          TOTAL ({preparedEmployees.length} Employees)
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(preparedEmployees.reduce((sum, e) => sum + e.monthly_salary, 0))}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(Object.values(employeeDeductions).reduce((sum, d) => sum + (d.advance_recovery || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(Object.values(employeeDeductions).reduce((sum, d) => sum + (d.pf_deduction || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(Object.values(employeeDeductions).reduce((sum, d) => sum + (d.esi_deduction || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(Object.values(employeeDeductions).reduce((sum, d) => sum + (d.absence_deduction || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(Object.values(employeeDeductions).reduce((sum, d) => sum + (d.other_deductions || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(
                            Object.values(employeeDeductions).reduce((sum, d) => 
                              sum + (d.advance_recovery || 0) + (d.pf_deduction || 0) + 
                              (d.esi_deduction || 0) + (d.absence_deduction || 0) + (d.other_deductions || 0), 0
                            )
                          )}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(
                            preparedEmployees.reduce((sum, emp) => {
                              const ded = employeeDeductions[emp.id] || {};
                              const totalDed = (ded.advance_recovery || 0) + (ded.pf_deduction || 0) + 
                                              (ded.esi_deduction || 0) + (ded.absence_deduction || 0) + 
                                              (ded.other_deductions || 0);
                              return sum + Math.max(0, emp.monthly_salary - totalDed);
                            }, 0)
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                
                <div className="flex gap-2 justify-end pt-4 border-t">
                  <Button variant="outline" onClick={() => {
                    setShowGenerateSalaries(false);
                    setPreparedEmployees([]);
                    setEmployeeDeductions({});
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handleGenerateSalaries}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Generate {preparedEmployees.length} Salaries
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryMaster;
